import { IFundManager } from '@kurtosys/types/api/dictionary/index.js';
import { IStatistics } from '@kurtosys/types/api/search/index.js';
import { IUser } from '@kurtosys/types/authentication/index.js';
import { IFormat } from '@kurtosys/types/data/index.js';
import { IQueryClassDependency } from '@kurtosys/types/dependencies/IQueryClassDependency.js';
import { ITransformClassDependency } from '@kurtosys/types/dependencies/ITransformClassDependency.js';
import { IPortfolio, IPortfolioEntity, IScenario, IScenarioReport } from '@kurtosys/types/portal/index.js';
import {
	DebugType,
	IExecutionOptions,
	IQueryContextExternalApiRequest,
	IQueryContextExternalApiResponse,
	IQueryOptions,
	IQueryOptionsSort,
} from '@kurtosys/types/query/index.js';
import { IQueryOptionsExternalApi } from '@kurtosys/types/query/options/externalApi/IQueryOptionsExternalApi.js';

import { commonUtils, helpers } from '../../../common/index.js';
import { applySort } from '../../queryUtils/applySort.js';
import { getQueryClass } from '../../queryUtils/getQueryClass.js';
import { queryHelper } from '../../queryUtils/index.js';

type TranslationHelper = helpers.TranslationHelper;

export const MAX_CALL_STACK_LEVEL = 50;

type TChildQueries<T> = {
	[K in keyof T]: T[K] extends IQueryOptions ? K : never;
}[keyof T];

type TNotChildQueries<T> = {
	[K in keyof T]: T[K] extends IQueryOptions ? never : K;
}[keyof T];

export abstract class BaseQuery<TChildOptions = any> {
	private debugBase(type: DebugType, ...messages: any[]): void {
		if (this.queryOptions && this.queryOptions.debug && this.queryOptions.debug.key) {
			const prefix = `QueryOptions Debug - "${this.queryOptions.debug.key}": `;
			console[type](prefix, ...messages);
		}
	}
	debug = {
		error: (...messages: any[]): void => {
			this.debugBase(DebugType.error, ...messages);
		},
		info: (...messages: any[]): void => {
			this.debugBase(DebugType.info, ...messages);
		},
		warn: (...messages: any[]): void => {
			this.debugBase(DebugType.warn, ...messages);
		},
	};

	// dependencies is used to inject the class to avoid circular referenced imports
	constructor(
		public dependencies: IQueryClassDependency & ITransformClassDependency,
		public queryOptions: IQueryOptions,
		public translationHelper?: TranslationHelper,
	) {}
	abstract getRawValue<T = any>(options: IExecutionOptions, callStackLevel?: number): T;
	abstract getOptions(): TChildOptions;
	rawOptions: TChildOptions;
	get options(): TChildOptions {
		if (!this.rawOptions) {
			this.rawOptions = this.getOptions();
		}
		return this.rawOptions;
	}
	fillKeyFromQuery(
		fillKey: TNotChildQueries<TChildOptions>,
		queryKey: TChildQueries<TChildOptions>,
		executionOptions: IExecutionOptions,
	): void {
		const childQueryOptions = (this.options && this.options[queryKey]) as any as IQueryOptions;
		if (this.options && childQueryOptions) {
			const query = getQueryClass(this.dependencies, childQueryOptions, this.translationHelper)
			if (query) {
				this.options[fillKey] = query.execute(executionOptions);
			}
		}
	}
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	initialize(executionOptions: IExecutionOptions): void {
		// Used to override
	}
	execute<T = unknown>(executionOptions: IExecutionOptions, callStackLevel = 1): T {
		if (callStackLevel > MAX_CALL_STACK_LEVEL) {
			console.warn('BaseQuery MAX_CALL_STACK_LEVEL exceeded');
			return;
		}
		this.debug.info('Initialize started');
		this.initialize(executionOptions);
		const { culture, placeholder: defaultPlaceholder, translate } = executionOptions;
		const {
			format,
			placeholder: staticPlaceholder = defaultPlaceholder,
			placeholderQuery,
			performanceCheckPlaceholder,
			transforms,
			sort,
		} = this.queryOptions;
		// Validate that the performance values are allowed and this is a performance value
		if (!this.passesPerformanceCheck(executionOptions)) {
			this.debug.info('Performance check failed');
			let performanceCheckPlaceholderValue;
			if (performanceCheckPlaceholder) {
				const queryForPerformancePlaceholder = getQueryClass(this.dependencies, performanceCheckPlaceholder, this.translationHelper)
				if (queryForPerformancePlaceholder) {
					performanceCheckPlaceholderValue = queryForPerformancePlaceholder.execute(executionOptions);
				}
			}
			return performanceCheckPlaceholderValue || (staticPlaceholder as any);
		}
		let rawValue = this.getRawValue(executionOptions, callStackLevel);
		this.debug.info('getRawValue = ', rawValue);
		if (commonUtils.isNullOrUndefined(rawValue)) {
			let placeholderValue;
			if (placeholderQuery) {
				const queryForPlaceholder = getQueryClass(this.dependencies, placeholderQuery, this.translationHelper);
				if (queryForPlaceholder) {
					placeholderValue = queryForPlaceholder.execute(executionOptions);
				}
			}
			if (!commonUtils.isNullOrUndefined(placeholderValue)) {
				rawValue = placeholderValue;
				this.debug.info('rawValue set to placeholderQuery');
			} else {
				rawValue = staticPlaceholder;
				this.debug.info('rawValue set to placeholder');
			}
		}

		if (transforms) {
			this.debug.info('has transforms');
			for (const transformOptions of transforms) {
				this.debug.info(`Starting transform of type: "${transformOptions.transformOptionsType}"`, {
					transformOptions,
				});
				this.debug.info(`rawValue before transform`, { rawValue });
				const transform = new this.dependencies.transformClass(
					this.dependencies,
					transformOptions,
					this.translationHelper,
					callStackLevel,
				);
				rawValue = transform.execute(rawValue, this.queryOptions, executionOptions);
				this.debug.info(`rawValue after transform`, { rawValue });
			}
		}

		if (format) {
			this.debug.info('has format');
			this.debug.info(`rawValue before format`, { rawValue });
			rawValue = this.applyFormat(format, rawValue, executionOptions);
			this.debug.info(`rawValue after format`, { rawValue });
		}

		if (translate && typeof rawValue === 'string') {
			this.debug.info('has translate');
			this.debug.info(`rawValue before translate`, { rawValue });
			rawValue = translate(rawValue) as any as T;
			this.debug.info(`rawValue after translate`, { rawValue });
			this.debug.info('response', { rawValue });
			return rawValue;
		}
		if (Array.isArray(rawValue) && sort) {
			this.debug.info('has sort');
			this.debug.info(`rawValue before sort`, { rawValue });
			rawValue = this.applySort(rawValue, sort, culture, executionOptions.inputs);
			this.debug.info(`rawValue after sort`, { rawValue });
		}
		this.debug.info('response', { rawValue });
		return rawValue;
	}
	applyFormat(format: IFormat, rawValue: any, executionOptions: IExecutionOptions): string | number {
		const { currency, culture, translate } = executionOptions;
		if (format.globalFormatKey) {
			const globalFormat =
				executionOptions &&
				executionOptions.globalFormatsByKey &&
				executionOptions.globalFormatsByKey[format.globalFormatKey];
			if (globalFormat && globalFormat.format) {
				format = commonUtils.deepMergeObjects(globalFormat.format, format);
			}
		}
		const { type, transforms, options, optionsByCulture = {} } = format;
		const optionByCulture = optionsByCulture[culture];
		const { subQueries = [] } = this.queryOptions;
		let workingCurrency = currency;
		if (subQueries) {
			const currencyQueryOptions = subQueries.find((subQuery) => subQuery.queryId === 'currency');
			if (currencyQueryOptions) {
				const currencyQuery = getQueryClass(this.dependencies, currencyQueryOptions, this.translationHelper)
				if (currencyQuery) {
					workingCurrency = currencyQuery.execute(executionOptions);
				}
			}
		}
		return commonUtils.spreadFormatter(
			type,
			culture,
			workingCurrency,
			optionByCulture || options,
			rawValue,
			transforms,
			translate,
		);
	}
	passesPerformanceCheck(executionOptions: IExecutionOptions): boolean {
		const context = queryHelper.getContext(executionOptions, this.queryOptions);
		const { isPerformanceAllowed = false } = context || {};
		const { checkPerformanceAllowed } = this.queryOptions;
		return !checkPerformanceAllowed || isPerformanceAllowed;
	}
	applySort(
		collection: any[],
		sorts: IQueryOptionsSort | IQueryOptionsSort[],
		culture?: string,
		inputs?: object,
	): any[] {
		return applySort({
			collection,
			sorts,
			culture,
			inputs,
			sorters: this.sorters,
			translationHelper: this.translationHelper,
		})
	}

	getScenarioFromContext(executionOptions: IExecutionOptions): IScenario | undefined {
		const context = queryHelper.getContext(executionOptions, this.queryOptions);
		if (context) {
			return context.scenario;
		}
		return;
	}

	getScenarioReportFromContext(executionOptions: IExecutionOptions): IScenarioReport | undefined {
		const context = queryHelper.getContext(executionOptions, this.queryOptions);
		if (context) {
			return context.scenarioReport;
		}
		return;
	}

	getPortfolioFromContext(executionOptions: IExecutionOptions): IPortfolio | undefined {
		const context = queryHelper.getContext(executionOptions, this.queryOptions);
		if (context) {
			return context.portfolio;
		}
		return;
	}

	getPortfolioEntityFromContext(executionOptions: IExecutionOptions): IPortfolioEntity | undefined {
		const context = queryHelper.getContext(executionOptions, this.queryOptions);
		if (context) {
			return context.portfolioEntity;
		}
		return;
	}

	getFundManager(executionOptions: IExecutionOptions): IFundManager | undefined {
		const { fundManagerContext } = executionOptions;
		if (fundManagerContext) {
			return fundManagerContext;
		}
		return;
	}

	getLoggedInUser(executionOptions: IExecutionOptions): IUser | undefined {
		const context = queryHelper.getContext(executionOptions, this.queryOptions);
		if (context && context.loggedInUser) {
			return context.loggedInUser;
		}
		return;
	}

	getStatistic(executionOptions: IExecutionOptions): IStatistics | undefined {
		const { statisticContext } = executionOptions;
		if (statisticContext) {
			return statisticContext;
		}
		return;
	}
	sorters = {
		date: (a: string | number, b: string | number): number => {
			const aDate = new Date(a);
			const bDate = new Date(b);
			return Number(aDate) - Number(bDate);
		},
		number: (a: number, b: number): number => {
			return a - b;
		},
		string: (a: string, b: string, culture): number => {
			const aString = a.toString().toUpperCase();
			const bString = b.toString().toUpperCase();
			return aString.localeCompare(bString, culture);
		},
	};

	getExternalApiResponse(
		externalApiOptions: IQueryOptionsExternalApi,
		executionOptions: IExecutionOptions,
	): IQueryContextExternalApiResponse | undefined {
		const context = queryHelper.getContext(executionOptions, this.queryOptions);
		if (context) {
			const { externalApiResponses } = context;
			if (externalApiResponses && externalApiOptions) {
				const { id } = externalApiOptions;
				return externalApiResponses[id];
			}
		}
		return;
	}

	getExternalApiRequest(
		externalApiOptions: IQueryOptionsExternalApi,
		executionOptions: IExecutionOptions,
	): IQueryContextExternalApiRequest | undefined {
		const contextConfiguration = queryHelper.getContextConfiguration(executionOptions, this.queryOptions);
		if (contextConfiguration) {
			const { id } = externalApiOptions;
			const { externalApiRequests = [] } = contextConfiguration;
			return externalApiRequests.find((externalApiRequest): boolean => externalApiRequest.id === id);
		}
		return;
	}
}
